"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useCookies } from "react-cookie"
import { useModal } from "../modal/Provdier"
import { useLogin, useUserInfo } from "@/hooks/useUser"
import { apiFetch } from "@/lib/apiFetch"

const DEFAULT_LOGO = "/images/kexin/logo.png"

export default function KexinHeader({ logo }: { logo?: string }) {
  const { setIsLoginOpen, setIsRegisterOpen } = useModal()
  const [userInfo] = useUserInfo()
  const { logout } = useLogin()
  const [cookies, setCookie, removeCookie] = useCookies(["token", "healthToken"])
  const [mounted, setMounted] = useState(false)
  const tokenHead = cookies.token?.tokenHead || ""
  const tokenValue = cookies.token?.token || ""
  const healthTokenRequestRef = useRef("")
  const authorization = useMemo(() => {
    return tokenValue ? `${tokenHead}${tokenValue}` : ""
  }, [tokenHead, tokenValue])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    let alive = true
    if (!authorization) return
    if (healthTokenRequestRef.current === authorization) return
    healthTokenRequestRef.current = authorization

    async function loadHealthToken() {
      try {
        const response = await apiFetch("/aiChat/chat/coze/getAccessToken", { authorization })
        const result = await response.json()
        if (!alive || !response.ok || result.code !== 0 || !result.data) return
        if (cookies.healthToken === result.data) return
        setCookie("healthToken", result.data, {
          path: "/",
          maxAge: 24 * 60 * 60,
          secure: true,
          sameSite: "none",
        })
      } catch {
        if (alive) {
          removeCookie("healthToken", { path: "/" })
        }
      }
    }

    loadHealthToken()

    return () => {
      alive = false
    }
  }, [authorization, cookies.healthToken, removeCookie, setCookie])

  function handleLogout() {
    removeCookie("healthToken", { path: "/" })
    logout()
  }

  const itemBase =
    "transition-all duration-500 " + (mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0")

  return (
    <header className="flex h-16 items-center justify-between px-4 md:h-20 md:px-8">
      <a href="#" className={`inline-flex h-8 items-center md:h-10 ${itemBase}`} aria-label="可信首页">
        <img
          src={logo || DEFAULT_LOGO}
          alt="可信"
          className="h-full w-auto max-w-[200px] object-contain md:max-w-[260px]"
        />
      </a>

      <nav className="flex items-center gap-2.5" aria-label="主导航">
        <a
          href="/chat/traceable-knowledge-search"
          className={`inline-flex h-10 items-center justify-center gap-1 rounded-full bg-white px-3 text-base font-medium text-[#131921] shadow-[0_2px_3px_rgba(0,0,0,0.1)] transition-all hover:-translate-y-px hover:shadow-[0_4px_10px_rgba(19,25,33,0.1)] ${itemBase} delay-100`}
        >
          <i className="font_family icon-Review text-2xl leading-none" aria-hidden="true" />
          <span className="hidden md:inline">对话</span>
        </a>

        {userInfo?.id ? (
          <div
            className={`group relative z-50 after:absolute after:-left-[180px] after:top-0 after:z-50 after:h-3 after:w-[220px] after:translate-y-10 after:content-[''] ${itemBase} delay-150`}
          >
            <button
              type="button"
              className="inline-flex size-10 items-center justify-center rounded-xl bg-white text-[#131921] shadow-[0_2px_3px_rgba(0,0,0,0.1)] transition-all hover:-translate-y-px hover:shadow-[0_4px_10px_rgba(19,25,33,0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f54eb] focus-visible:ring-offset-2"
              aria-label="账户"
            >
              <i className="font_family icon-NFTuser text-2xl leading-none" aria-hidden="true" />
            </button>
            <div className="invisible pointer-events-none absolute right-0 top-0 z-50 w-[220px] translate-y-10 opacity-0 transition-[transform,opacity] duration-200 group-hover:visible group-hover:pointer-events-auto group-hover:translate-y-[52px] group-hover:opacity-100 group-focus-within:visible group-focus-within:pointer-events-auto group-focus-within:translate-y-[52px] group-focus-within:opacity-100">
              <div className="overflow-hidden rounded-xl border border-[#e4e7ed] bg-white text-sm shadow-[0_12px_30px_-18px_rgba(0,0,0,0.35)]">
                <div className="bg-[#f5f7fa] px-4 py-3 text-[#131921]">
                  <div className="font-bold">{userInfo.nickName || "可信用户"}</div>
                  <div className="mt-1 truncate text-xs text-[#707782]">{userInfo.phone}</div>
                </div>
                <div className="p-2">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full rounded-lg px-3 py-2 text-left font-medium text-[#131921] transition-colors hover:bg-[#f5f7fa]"
                  >
                    退出登录
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setIsLoginOpen(true)}
              className={`inline-flex h-10 items-center justify-center rounded-full bg-white px-4 text-base font-medium text-[#131921] shadow-[0_2px_3px_rgba(0,0,0,0.1)] transition-all hover:-translate-y-px hover:shadow-[0_4px_10px_rgba(19,25,33,0.1)] ${itemBase} delay-150`}
            >
              登录
            </button>
            <button
              type="button"
              onClick={() => setIsRegisterOpen(true)}
              className={`hidden h-10 items-center justify-center rounded-full bg-[#131921] px-4 text-base font-medium text-white shadow-[0_2px_3px_rgba(0,0,0,0.1)] transition-all hover:-translate-y-px hover:shadow-[0_4px_10px_rgba(19,25,33,0.2)] md:inline-flex ${itemBase} delay-200`}
            >
              注册
            </button>
          </>
        )}
      </nav>
    </header>
  )
}
